import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { useImageExists } from "../hooks/useImageExists";
import { Layout } from "@components/Layout";
import { useParams } from "react-router-dom";
import { languageEnglish } from "../i18n";
import { getPageSlugFromPathname } from "../utils/getPageSlugFromPathname";

interface ErrorPageProps {
  errorCode: string;
  /**
   * When rendered in place of a failed draft inside the Storyblok Visual Editor, show
   * editor-aware copy instead of the generic error message. See `useEditorPreviewFallback`.
   */
  isEditorPreview?: boolean;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
  errorCode,
  isEditorPreview = false,
}) => {
  const imagePath = `/${errorCode}.svg`;
  const imageAvailable = useImageExists(imagePath);
  const { t } = useTranslation();
  const { lang } = useParams();

  const pageSlug = getPageSlugFromPathname(location.pathname);

  const message = isEditorPreview
    ? t("ErrorPage.editorPreviewMessage")
    : t(`ErrorPage.message${errorCode}`);

  return (
    <Layout
      translatedSlug={
        lang === languageEnglish
          ? `/${pageSlug}`
          : `/${languageEnglish}/${pageSlug}`
      }
      hideWidgets={errorCode === "403"}
      hideNavigation={errorCode === "403"}
    >
      <title>{t("ErrorPage.title")}</title>

      <div className="flex h-[calc(100vh-3.75rem)] flex-col overflow-clip [--text-padding:--spacing(4)] md:h-[calc(100vh-4.25rem)] lg:[--text-padding:--spacing(12)]">
        <div className="flex flex-grow items-center justify-center bg-secondary-900 dark:bg-secondary-950">
          {errorCode && (
            <Typography
              size="2xl"
              className="max-w-[80vw] text-center text-pretty text-white lg:max-w-md lg:text-4xl"
            >
              {message}
            </Typography>
          )}
        </div>

        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center bg-white dark:bg-transparent">
          {imageAvailable ? (
            <img
              src={imagePath}
              className="-mt-[var(--text-padding)] h-auto w-full"
              alt={`${errorCode} error`}
            />
          ) : (
            <Typography
              as="h1"
              size="9xl"
              className="trim-both-cap-alphabetic -mt-[var(--text-padding)] text-[30vw] text-primary-500"
            >
              {errorCode}
            </Typography>
          )}
        </div>
      </div>
    </Layout>
  );
};
